<template>
  <div class="input-container">
    <div v-if="messageQueue.length > 0" class="queue-section">
      <div class="queue-section-title">Queued ({{ messageQueue.length }})</div>
      <div class="queue-list">
        <div v-for="(item, index) in messageQueue" :key="index" class="queue-item">
          <span class="queue-item-text">{{ truncateQueueText(item) }}</span>
          <span class="button queue-remove" @click="removeFromQueue(index)"><font-awesome-icon icon="xmark"></font-awesome-icon></span>
        </div>
      </div>
    </div>
    <div class="input-row">
      <textarea ref="userInput" v-model="messageText" @keyup="adjustTextareaHeight" @keydown.up="handleUp" @keydown.down="handleDown" @keydown.shift.enter.prevent="newLine" @keydown.enter.exact.stop.prevent="sendMessage"
        type="text"/>

      <div v-if="awaitingResponse" class="button send-button" @click="cancelMessage"><font-awesome-icon size="1x" icon="stop"></font-awesome-icon></div>
      <div v-else class="button send-button" @click="sendMessage"><font-awesome-icon size="1x" icon="arrow-right"></font-awesome-icon></div>
    </div>
  </div>
</template>

<script>

import { mapState } from 'vuex'
export default {
  name: "ChatInput",
  data() {
    return {
      messageText: "",
      height: 0,
      currentMessage: {},
      queueSendTimeoutId: null
    }
  },
  computed: {
    ...mapState({
      messages: state => state.chat.messages.all,
      session: state => state.chat.currentSession,
      messageQueue: state => state.chat.messageQueue
    }),
    userMessages() {
      return this.messages.filter((m) => !m.author.isBot && !m.__pending)
    },
    currentMessageIndex() {
      if (this.currentMessage.id === undefined) {
        return this.userMessages.length
      }
      const idx = this.userMessages.map((m) => m.id).indexOf(this.currentMessage.id)
      if (idx === -1) {
        return this.userMessages.length
      }
      return idx
    },
    awaitingResponse() {
      return this.processingCount > 0
    },
    processingCount() {
      return this.messages.filter((m) => {
        if (!m.author.isBot || m.text !== "Processing...") return false
        const latest = m.history && Array.isArray(m.history) && m.history.length > 0 ? m.history.slice(-1)[0] : null
        const latestStatus = latest && typeof latest === "object" ? latest.status : null
        return latestStatus !== "cancelled" && latestStatus !== "cancelling"
      }).length
    }
  },
  mounted() {
    this.$nextTick(() => {
      const input = this.$refs && this.$refs.userInput
      if (input) {
        input.focus()
        this.height = Math.ceil(input.scrollHeight < 44 ? 44 : input.scrollHeight)
        input.style.height = this.height + 'px'
      }
    })
  },
  methods: {
    /** Clears history navigation so the next ArrowUp starts from the latest user message. */
    resetInputHistoryPointer() {
      this.currentMessage = {}
    },
    up() {
      const foundMessage = this.userMessages[this.currentMessageIndex - 1]
      if (foundMessage !== undefined) {
        this.currentMessage = foundMessage
        this.messageText = (foundMessage.text || "").trim()
        this.$nextTick(() => this.setCaretToEnd())
      }
    },
    down() {
      const foundMessage = this.userMessages[this.currentMessageIndex + 1]
      if (foundMessage !== undefined) {
        this.currentMessage = foundMessage
        this.messageText = (foundMessage.text || "").trim()
        this.$nextTick(() => this.setCaretToEnd())
      } else {
        this.messageText = ""
        this.resetInputHistoryPointer()
      }
    },
    setCaretToEnd() {
      const input = this.$refs && this.$refs.userInput
      if (input) {
        const len = this.messageText.length
        input.setSelectionRange(len, len)
        input.focus()
      }
    },
    adjustTextareaHeight() {
      const h = this.$refs && this.$refs.userInput
      if (h) {
        h.style.height = "1px"
        h.style.height = h.scrollHeight + "px"
      }
    },
    async sendMessage() {
      const text = this.messageText.trim()
      if (text === "") {
        return
      }
      if (this.session.id === undefined) {
        const chatSession = await this.$store.dispatch("createChatSession")
        this.messageText = ""
        this.resetInputHistoryPointer()
        this.$emit("user-message-submitted")
        this.$router.push({ name: "ChatSession", params: { sessionId: chatSession.id } })
        try {
          await this.$store.dispatch("sendChatMessage", { text, sessionId: chatSession.id })
        } catch (err) {
          // Error surfaced via flash message in store
        }
      } else if (this.awaitingResponse || this.messageQueue.length > 0) {
        this.$store.commit("enqueueMessage", text)
        this.messageText = ""
        this.resetInputHistoryPointer()
        this.$emit("user-message-submitted")
      } else {
        this.messageText = ""
        this.resetInputHistoryPointer()
        this.$emit("user-message-submitted")
        try {
          await this.$store.dispatch("sendChatMessage", { text, sessionId: this.session.id })
        } catch (err) {
          // Error surfaced via flash message in store
        }
      }
    },
    removeFromQueue(index) {
      this.$store.commit("removeQueuedMessage", index)
    },
    truncateQueueText(text) {
      if (!text) return ""
      const oneLine = text.replace(/\s+/g, " ").trim()
      const maxLen = 60
      if (oneLine.length <= maxLen) return oneLine
      return oneLine.slice(0, maxLen) + "\u2026"
    },
    handleUp(event) {
      const cursorPosition = this.$refs.userInput.selectionStart
      const firstNl = this.messageText.indexOf("\n")
      const onFirstLine =
        cursorPosition === 0 || (firstNl !== -1 && cursorPosition < firstNl)

      if (onFirstLine) {
        event.preventDefault()
        this.up()
      }
    },
    handleDown(event) {
      const textarea = this.$refs.userInput
      const cursorPosition = textarea.selectionStart
      const lastNl = this.messageText.lastIndexOf("\n")
      const onLastLine = lastNl === -1 ? true : cursorPosition > lastNl

      if (onLastLine) {
        event.preventDefault()
        this.down()
      }
    },
    newLine() {
      const input = this.$refs && this.$refs.userInput
      if (!input) {
        return
      }
      const start = input.selectionStart
      const end = input.selectionEnd
      const text = this.messageText
      this.messageText = text.slice(0, start) + "\n" + text.slice(end)
      this.$nextTick(() => {
        const pos = start + 1
        input.setSelectionRange(pos, pos)
        input.focus()
        this.adjustTextareaHeight()
      })
    },
    async cancelMessage() {
      const processingMessages = (this.messages || []).filter((m) => m.author && m.author.isBot && m.text === "Processing...")
      const processingMessage = processingMessages.length ? processingMessages[processingMessages.length - 1] : null
      if (!processingMessage) {
        return
      }
      try {
        await this.$store.dispatch("addChatMessageStatus", { id: processingMessage.id, status: '{"status": "cancelling"}' })
      } catch (err) {
        const detail = (err && err.response && (err.response.lowLevelErrorMessage || err.response.message)) || (err && err.message) || "Unknown error"
        console.error("Failed to cancel chat message:", err, "detail:", detail)
        this.$store.commit("displayFlashMessage", {
          message: detail || "Unable to cancel. Please try again.",
          severity: "error",
        })
      }
    }
  },
  beforeDestroy() {
    if (this.queueSendTimeoutId !== null) {
      window.clearTimeout(this.queueSendTimeoutId)
    }
  },
  watch: {
    processingCount(newVal, oldVal) {
      if (oldVal === 1 && newVal === 0 && this.messageQueue.length > 0) {
        if (this.queueSendTimeoutId !== null) {
          window.clearTimeout(this.queueSendTimeoutId)
        }
        this.queueSendTimeoutId = window.setTimeout(() => {
          this.queueSendTimeoutId = null
          if (this.messageQueue.length > 0 && this.processingCount === 0) {
            this.$store.dispatch("sendFirstQueuedMessage")
          }
        }, 120)
      } else if (newVal > 0 && this.queueSendTimeoutId !== null) {
        window.clearTimeout(this.queueSendTimeoutId)
        this.queueSendTimeoutId = null
      }
    },
    messageText() {
      this.$nextTick(() => {
        const userInput = this.$refs && this.$refs.userInput
        if (!userInput) return
        this.height = Math.ceil(userInput.scrollHeight < 44 ? 44 : userInput.scrollHeight)
        userInput.style.height = this.height + 'px'
      })
    },
    'session.id'(newId, oldId) {
      if (newId !== oldId) {
        this.resetInputHistoryPointer()
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.input-container {
  width: 100%;
  padding: 5px 10px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  font-size: 1.3em;
  line-height: 1.3em;

  margin-bottom: 20px;

  max-height: 200px;
}

.queue-section {
  flex-shrink: 0;
  padding: 4px 0;
  border-bottom: 1px solid #dddddd; 
  width: 90%;
  margin: 0 auto;
}

.queue-section-title {
  font-size: 0.85em;
  font-weight: 600;
  color: #666;
  margin-bottom: 6px;
}

.queue-list {
  overflow-y: auto;
  max-height: 100px;
}

.queue-item {
  display: flex;
  align-items: center;
  font-size: 0.8em;
  padding: 2px 0;
}

.queue-item-icon {
  flex-shrink: 0;
  margin-right: 8px;
  color: #666;
}

.queue-item-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
}

.queue-remove {
  flex-shrink: 0;
  margin-left: 8px;
  color: #999;
  cursor: pointer;
}

.queue-remove:hover {
  color: #333;
}

.input-row {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex: 1;
  border-radius: 15px;
  border: 1px solid #ccc;
  padding: 6px 8px;
  gap: 4px;
}

.input-row textarea {
  border: 0;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 44px;
  max-height: calc(100px - 6px);
  resize: none;
  margin: 4px 0 4px 4px;
}

.button {
  color: black;
  height: 100%;
  padding: 10px;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
}

.input-row .button.send-button {
  flex-shrink: 0;
  align-self: flex-end;
  box-sizing: border-box;
  width: 34px;
  min-width: 34px;
  height: 34px;
  min-height: 34px;
  padding: 0;
  margin: 0 2px 4px 0;
  font-size: 15px;
}
</style>